export const privateParticipantCompanyFilter = 'private' as const;

export type AttendeeCompanyFilter =
  | number
  | typeof privateParticipantCompanyFilter;

type CompanyBearer = {
  clientCompanyId?: number;
  clientCompanyName?: string;
};

export const attendeeCompanyFilterOptions = (
  attendees: CompanyBearer[],
  privateParticipantLabel: string,
): { label: string; value: AttendeeCompanyFilter }[] => {
  const companies = new Map<number, string>();
  let hasPrivateParticipants = false;

  attendees.forEach(({ clientCompanyId, clientCompanyName }) => {
    if (clientCompanyId != null && clientCompanyName) {
      companies.set(clientCompanyId, clientCompanyName);
    } else hasPrivateParticipants = true;
  });

  const options: { label: string; value: AttendeeCompanyFilter }[] = [
    ...companies,
  ]
    .sort(
      (left, right) => left[1].localeCompare(right[1]) || left[0] - right[0],
    )
    .map(([companyId, companyName]) => ({
      label: companyName,
      value: companyId,
    }));

  if (hasPrivateParticipants) {
    options.push({
      label: privateParticipantLabel,
      value: privateParticipantCompanyFilter,
    });
  }

  return options;
};

export const filterAttendeesByCompany = <T extends CompanyBearer>(
  attendees: T[],
  companyFilters: AttendeeCompanyFilter[] | null,
): T[] => {
  if (!companyFilters?.length) return attendees;

  const selectedCompanies = new Set(companyFilters);
  return attendees.filter(({ clientCompanyId }) =>
    clientCompanyId == null
      ? selectedCompanies.has(privateParticipantCompanyFilter)
      : selectedCompanies.has(clientCompanyId),
  );
};

export const compareAttendeesByCompany = <
  T extends CompanyBearer & { name: string },
>(
  left: T,
  right: T,
): number => {
  const leftIsPrivate = left.clientCompanyId == null;
  const rightIsPrivate = right.clientCompanyId == null;

  if (leftIsPrivate !== rightIsPrivate) return leftIsPrivate ? -1 : 1;

  return (
    (left.clientCompanyName ?? '').localeCompare(
      right.clientCompanyName ?? '',
    ) ||
    (left.clientCompanyId ?? 0) - (right.clientCompanyId ?? 0) ||
    left.name.localeCompare(right.name)
  );
};

export const sortAttendeesByCompany = <
  T extends CompanyBearer & { name: string },
>(
  attendees: T[],
): T[] => [...attendees].sort(compareAttendeesByCompany);
